import React, { useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import DashboardTopBarAdmin from "../../components/admin/DashboardTopBarAdmin";
import SuccesButton from "../../components/shared/SuccessButton";
import Modal from "../../components/shared/Modal";
import KeyValueTable from "../../components/shared/KeyValueTable";
import {
   fetchOpeningHours,
   fetchPickupMoments,
   fetchVacations,
   deleteVacationMode,
   queryClient,
} from "../../util/admin/httpAdmin";
import pickupBlueIcon from "../../assets/admin/icons/pickupslots-blue.svg";
import editIconWhite from "../../assets/admin/icons/edit-white.svg";
import PickupSlotDayForm from "../../components/admin/pickupslots/PickupSlotDayForm";
import VacationModeForm from "../../components/admin/pickupslots/VacationModeForm";
import PrimaryButton from "../../components/shared/PrimaryButton";
import plusIconWhite from "../../assets/admin/icons/plus-white.svg";
import "./tab-styling.css";
import { useMutation } from "@tanstack/react-query";
import deleteIconWhite from "../../assets/admin/icons/delete-white.svg";
import ErrorButton from "../../components/shared/ErrorButton";

// Functie om de datum om te zetten naar dd/mm/yyyy
const formatDate = (date) => {
   const d = new Date(date);
   return `${d.getDate().toString().padStart(2, "0")}/${(d.getMonth() + 1)
      .toString()
      .padStart(2, "0")}/${d.getFullYear()}`;
};

function PickupMomentsAdmin() {
   const modalRef = useRef();
   const vacationModalRef = useRef();
   const [editMode, setEditMode] = useState(false);
   const [dayToEdit, setDayToEdit] = useState("maandag");
   const [vacationMode, setVacationMode] = useState(false);
   const [activeTab, setActiveTab] = useState("pickup");

   const [vacationToDelete, setVacationToDelete] = useState(null);

   const confirmationModalRef = useRef();

   // Ophalen van ophaalmomenten
   const {
      data: pickupData,
      isLoading: loadingPickups,
      isError: errorPickups,
      error: errorPickupData,
   } = useQuery({
      queryKey: ["pickupMoments"],
      queryFn: fetchPickupMoments,
   });

   // Ophalen van openingsuren
   const {
      data: openingHoursData,
      isLoading: loadingOpeningHours,
      isError: errorOpeningHours,
      error: errorOpeningHoursData,
   } = useQuery({
      queryKey: ["openinghours"],
      queryFn: fetchOpeningHours,
   });

   // Ophalen van vakantiemodus
   const {
      data: vacationData,
      isLoading: loadingVacations,
      isError: errorVacations,
      error: errorVacationData,
   } = useQuery({
      queryKey: ["vacationModes"],
      queryFn: fetchVacations,
   });

   const deleteMutation = useMutation({
      mutationFn: deleteVacationMode,
      onSuccess: () => {
         queryClient.invalidateQueries(["vacationModes"]);
         confirmationModalRef.current.close();
      },
   });

   if (loadingPickups || loadingOpeningHours || loadingVacations) {
      return <p>Gegevens laden...</p>;
   }

   if (errorPickups || errorOpeningHours || errorVacations) {
      return (
         <div>
            <p>Er is een fout opgetreden bij het laden van de gegevens:</p>
            {errorPickupData && <p>{errorPickupData.message}</p>}
            {errorOpeningHoursData && <p>{errorOpeningHoursData.message}</p>}
            {errorVacationData && <p>{errorVacationData.message}</p>}
         </div>
      );
   }

   // Sluit de modal
   const closeModal = () => {
      if (modalRef.current?.close) {
         modalRef.current.close();
      }
   };

   // Open de modal voor ophaalmomenten en stel de te bewerken dag in
   const openModal = (day, type) => {
      setDayToEdit(day);

      let dayData;

      if (type === "PICKUP") {
         dayData = pickupData?.[day];
      } else if (type == "OPENINGHOURS") {
         dayData = openingHoursData?.[day];
      }

      if (!dayData) {
         dayData = [];
      }

      setEditMode(dayData.length > 0);

      if (modalRef.current?.open) {
         modalRef.current.open();
      } else {
         console.error("Modal referentie is niet correct geïnitieerd.");
      }
   };

   // Functie om de data om te zetten naar het juiste formaat voor de KeyValueTable
   const transformData = (dayData, type) => {
      return dayData.reduce((acc, { start, end }, index) => {
         const key = `${type} - ${index + 1}`;
         const value = `${start} - ${end}`;
         acc[key] = value;
         return acc;
      }, {});
   };

   // Open de modal voor vakantiemodus
   const openVacationModal = () => {
      if (vacationModalRef.current?.open) {
         vacationModalRef.current.open();
      }
   };

   // Open de modal voor vakantiemodus en stel de bewerkmodus in
   const openVacationEditModal = (vacation) => {
      setVacationMode(vacation);
      openVacationModal();
   };

   const openDeleteConfirmationModal = (vacation) => {
      setVacationToDelete(vacation);
      confirmationModalRef.current.open();
   };

   return (
      <div>
         <DashboardTopBarAdmin
            title="Ophaalmomenten"
            icon={pickupBlueIcon}
            description="Beheer hier de beschikbare ophaalmomenten"
         ></DashboardTopBarAdmin>

         <div className="py-20 px-20 g-20">
            {/* Tabbladen */}
            <div className="tabs">
               <button
                  className={activeTab === "pickup" ? "active" : ""}
                  onClick={() => setActiveTab("pickup")}
               >
                  Ophaalmomenten
               </button>
               <button
                  className={activeTab === "vacation" ? "active" : ""}
                  onClick={() => setActiveTab("vacation")}
               >
                  Vakantiemodus
               </button>
               <button
                  className={activeTab === "openinghours" ? "active" : ""}
                  onClick={() => setActiveTab("openinghours")}
               >
                  Openingsuren
               </button>
            </div>

            {/* Weergave van tabinhoud */}
            {activeTab === "pickup" && (
               <div className="g-30">
                  {[
                     "maandag",
                     "dinsdag",
                     "woensdag",
                     "donderdag",
                     "vrijdag",
                     "zaterdag",
                     "zondag",
                  ].map((day) => (
                     <div key={day}>
                        <div className="g-10">
                           <h3>{day.charAt(0).toUpperCase() + day.slice(1)}</h3>
                           {pickupData?.[day]?.length > 0 ? (
                              <KeyValueTable
                                 className="f-s"
                                 data={transformData(
                                    pickupData[day],
                                    "Ophaalmoment"
                                 )}
                              />
                           ) : (
                              <p>Geen ophaalmomenten ingesteld {day}.</p>
                           )}
                           <div>
                              <SuccesButton
                                 onClick={() => openModal(day, "PICKUP")}
                              >
                                 <div className="g-15-1fr f-s">
                                    <img src={editIconWhite} alt="bewerken" />
                                    {day.charAt(0).toUpperCase() + day.slice(1)}
                                 </div>
                              </SuccesButton>
                           </div>
                        </div>
                     </div>
                  ))}
               </div>
            )}
            {activeTab === "openinghours" && (
               <div className="g-30">
                  {[
                     "maandag",
                     "dinsdag",
                     "woensdag",
                     "donderdag",
                     "vrijdag",
                     "zaterdag",
                     "zondag",
                  ].map((day) => (
                     <div key={day}>
                        <div className="g-10">
                           <h3>{day.charAt(0).toUpperCase() + day.slice(1)}</h3>
                           {openingHoursData?.[day]?.length > 0 ? (
                              <KeyValueTable
                                 className="f-s"
                                 data={transformData(
                                    openingHoursData[day],
                                    "Tijdsblok"
                                 )}
                              />
                           ) : (
                              <p>Geen openingsuren ingesteld {day}.</p>
                           )}
                           <div>
                              <SuccesButton
                                 onClick={() => openModal(day, "OPENINGHOURS")}
                              >
                                 <div className="g-15-1fr f-s">
                                    <img src={editIconWhite} alt="bewerken" />
                                    {day.charAt(0).toUpperCase() + day.slice(1)}
                                 </div>
                              </SuccesButton>
                           </div>
                        </div>
                     </div>
                  ))}
               </div>
            )}

            {activeTab === "vacation" && (
               <div className="g-20">
                  <div>
                     <PrimaryButton
                        onClick={() => {
                           setVacationMode(false);
                           openVacationModal();
                        }}
                     >
                        <div className="g-15-1fr f-s">
                           <img
                              src={plusIconWhite}
                              alt="vakantiemodus toevoegen"
                           />
                           Vakantiemodus toevoegen
                        </div>
                     </PrimaryButton>
                  </div>

                  {vacationData && vacationData.length > 0 ? (
                     vacationData.map((vacation) => (
                        <div key={vacation._id} className="g-10">
                           <div className="g-10">
                              {/* Formatteer de datums voordat ze worden weergegeven */}
                              <h3>
                                 Vakantiemodus van{" "}
                                 {formatDate(vacation.vacationModeStart)} tot{" "}
                                 {formatDate(vacation.vacationModeEnd)}
                              </h3>
                              <div>
                                 <SuccesButton
                                    onClick={() =>
                                       openVacationEditModal(vacation)
                                    }
                                 >
                                    <div className="g-15-1fr f-s">
                                       <img
                                          src={editIconWhite}
                                          alt="bewerken"
                                       />
                                       Bewerken
                                    </div>
                                 </SuccesButton>
                              </div>
                              <div>
                                 <ErrorButton
                                    onClick={() =>
                                       openDeleteConfirmationModal(vacation)
                                    }
                                 >
                                    <div className="g-15-1fr f-s">
                                       <img
                                          src={deleteIconWhite}
                                          alt="verwijderen"
                                       />
                                       Verwijderen
                                    </div>
                                 </ErrorButton>
                              </div>
                           </div>
                        </div>
                     ))
                  ) : (
                     <p>Geen vakantiemodus ingesteld.</p>
                  )}
               </div>
            )}
         </div>

         {/* Modal voor het bewerken van ophaalmomenten */}
         {(activeTab === "pickup" || activeTab === "openinghours") && (
            <Modal ref={modalRef} key={dayToEdit}>
               <PickupSlotDayForm
                  onClose={closeModal}
                  editMode={editMode}
                  onSuccessClose={closeModal}
                  day={dayToEdit}
                  type={activeTab}
                  previousData={
                     activeTab === "pickup"
                        ? pickupData?.[dayToEdit] || []
                        : openingHoursData?.[dayToEdit] || []
                  }
               />
            </Modal>
         )}

         {/* Modal voor het instellen van vakantiemodus */}
         {activeTab === "vacation" && (
            <>
               <Modal ref={vacationModalRef}>
                  <VacationModeForm
                     onSuccessClose={() => {
                        vacationModalRef.current.close();
                     }}
                     editMode={vacationMode}
                     previousData={vacationMode || {}}
                  />
               </Modal>

               {/* Bevestigingsmodal voor het verwijderen */}
               <Modal ref={confirmationModalRef}>
                  <div className="g-20">
                     <h3>
                        Weet je zeker dat je deze vakantiemodus wilt
                        verwijderen?
                     </h3>
                     <div className="g-10">
                        <div>
                           <ErrorButton
                              onClick={() => {
                                 if (vacationToDelete) {
                                    deleteMutation.mutate(vacationToDelete._id);
                                 }
                              }}
                           >
                              Verwijderen
                           </ErrorButton>
                        </div>
                        <div>
                           <PrimaryButton
                              onClick={() => {
                                 confirmationModalRef.current.close();
                              }}
                           >
                              Annuleren
                           </PrimaryButton>
                        </div>
                     </div>
                  </div>
               </Modal>
            </>
         )}
      </div>
   );
}

export default PickupMomentsAdmin;
